/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const Seo = ({title, description, noIndex, children, ...props}) => {
    const {rennaiRootUrl, rennaiLogoUrl} = getConfig()
    const parsedTitle = useMemo(() => {
        const _title =
            title ||
            `Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada`

        return !_title?.endsWith(' | Rennaï') ? `${_title} | Rennaï` : _title
    }, [title])

    const parsedDescription = useMemo(
        () =>
            description ||
            `Discover Rennaï, Montréal's top destination for self-care and beauty. Browse our exclusive range of luxury skincare, wellness products, and more for your ultimate self-care experience.`,
        [description]
    )

    const googleFontApiUrl = 'https://fonts.googleapis.com'
    const googleFontUrl = 'https://fonts.gstatic.com'

    return (
        <Helmet
            {...props}
            onChangeClientState={(_newState, {linkTags = []}) => {
                if (linkTags.length === 2)
                    linkTags?.map((link) => {
                        const items = document.querySelectorAll(
                            `link[hreflang="${link.hreflang}"]`
                        )
                        Object.values(items)?.[0]?.remove()
                    })
            }}
        >
            <link rel="preconnect" href={googleFontApiUrl} />
            <link rel="preconnect" href={googleFontUrl} crossOrigin="true" />
            <link
                href={`${googleFontApiUrl}/css2?family=Raleway:ital,wght@0,100..900;1,100..900&display=swap`}
                rel="stylesheet"
            />

            <link rel="preconnect" href={googleFontApiUrl} />
            <link rel="preconnect" href={googleFontUrl} crossOrigin="true" />
            <link
                href={`${googleFontApiUrl}/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap`}
                rel="stylesheet"
            />

            <title>{parsedTitle}</title>
            <meta name="title" content={parsedTitle} />
            <meta name="description" content={parsedDescription} />

            {/* Facebook Meta Tags */}
            <meta property="og:url" content={rennaiRootUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={parsedTitle} />
            <meta property="og:description" content={parsedDescription} />
            <meta property="og:image" content={rennaiLogoUrl} />

            {/* Twitter Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta property="twitter:domain" content={rennaiRootUrl} />
            <meta property="twitter:url" content={rennaiRootUrl} />
            <meta name="twitter:title" content={parsedTitle} />
            <meta name="twitter:description" content={parsedDescription} />
            <meta name="twitter:image" content={rennaiLogoUrl} />

            {/* No index - part */}
            {noIndex && <meta name="robots" content="noindex" />}
            {children}
        </Helmet>
    )
}

Seo.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    noIndex: PropTypes.bool,
    children: PropTypes.node
}

export default Seo
